import { useMemo } from 'react';
import { create } from 'zustand';
import { useCurrentUserId } from '@/entities/session';

/**
 * Priority stages and defaults — ported verbatim from cpanel constants.
 * NOTE: DEFAULT order [7,10,8,11] differs from the IDS list [7,8,10,11];
 * rebuilding the default from the IDS list would silently change the order.
 */
export const PRIORITY_STAGE_IDS = [7, 8, 10, 11] as const;
export const DEFAULT_STATUS_PRIORITY = [7, 10, 8, 11] as const;

const storageKey = (userId: string | number): string =>
  `orders.statusPriorityOrder.${userId}`;

function readFromStorage(key: string): number[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((n): n is number => typeof n === 'number');
  } catch {
    return [];
  }
}

/**
 * Shared store of the saved per-user priority order, keyed by userId. Backed by
 * a Zustand store (NOT per-hook `useState`) so the dialog's save is immediately
 * visible to the table widget that derives the list filter — they must agree.
 * localStorage is the durable backing (the cpanel contract).
 */
interface PriorityStore {
  /** Saved order per userId ([] = not customized). */
  byUser: Record<string, number[]>;
  load: (userId: string | number) => void;
  save: (userId: string | number, next: number[]) => void;
  reset: (userId: string | number) => void;
}

const usePriorityStore = create<PriorityStore>((set, get) => ({
  byUser: {},
  load: (userId) => {
    const id = String(userId);
    if (get().byUser[id] !== undefined) return;
    set((s) => ({ byUser: { ...s.byUser, [id]: readFromStorage(storageKey(id)) } }));
  },
  save: (userId, next) => {
    const id = String(userId);
    try {
      localStorage.setItem(storageKey(id), JSON.stringify(next));
    } catch {
      /* ignore */
    }
    set((s) => ({ byUser: { ...s.byUser, [id]: next } }));
  },
  reset: (userId) => {
    const id = String(userId);
    try {
      localStorage.removeItem(storageKey(id));
    } catch {
      /* ignore */
    }
    set((s) => ({ byUser: { ...s.byUser, [id]: [] } }));
  },
}));

export interface StatusPriorityState {
  /** The raw saved order ([] when not customized). */
  order: number[];
  /** The effective order — DEFAULT when not customized. */
  effectiveOrder: number[];
  isCustomized: boolean;
  save: (next: number[]) => void;
  reset: () => void;
}

/**
 * Per-user status-priority ordering. Ported from cpanel `useStatusPriorityOrder`
 * (per-hook useState → shared store for cross-component consistency).
 */
export function useStatusPriority(): StatusPriorityState {
  const userId = useCurrentUserId();
  const id = String(userId);
  const order = usePriorityStore((s) => s.byUser[id]);
  const saveFn = usePriorityStore((s) => s.save);
  const resetFn = usePriorityStore((s) => s.reset);
  const loadFn = usePriorityStore((s) => s.load);

  // Lazy-load this user's persisted order once.
  if (order === undefined) loadFn(userId);

  return useMemo(() => {
    const resolved = order ?? [];
    const isCustomized = resolved.length > 0;
    return {
      order: resolved,
      effectiveOrder: isCustomized ? resolved : [...DEFAULT_STATUS_PRIORITY],
      isCustomized,
      save: (next: number[]) => saveFn(userId, next),
      reset: () => resetFn(userId),
    };
  }, [order, saveFn, resetFn, userId]);
}
